import { Injectable } from "@angular/core";

import { SessionResp } from "@models";
import { Calculator } from "@utils";

interface Config {
  value?: string;
  fallback?: string;
  full?: boolean;
  odds?: boolean;
}

@Injectable({ providedIn: "root" })
export class CurrencyService {
  private _code: string;
  private _symbol: string;
  private _precision: number;

  public initCurrency(resp: SessionResp): void {
    this._code = resp.currency;
    this._precision = resp.currency_precision ?? 2;
    this._symbol =
      resp.currency_symbol ||
      Intl.NumberFormat("en", {
        style: "currency",
        currency: resp.currency,
      })
        .formatToParts()
        .find(part => part.type === "currency")?.value ||
      resp.currency ||
      "";
  }

  public get precision(): number {
    return this._precision;
  }

  public get code(): string {
    return this._code;
  }

  public get symbol(): string {
    return this._symbol;
  }

  public round({ value, fallback, full, odds }: Config): string {
    if (!value) {
      return fallback || "-";
    }

    const precision = odds ? 2 : this.precision;

    const out = Calculator._out;
    const td = Calculator._td;
    const C0 = Calculator.C0; // 48
    const CDOT = Calculator.CDOT; // 46
    const len = value.length;

    let wi = 0;
    let dotAt = -1;
    let fracWritten = 0;
    let lastNonZeroFrac = -1;

    for (let i = 0; i < len; i++) {
      const c = value.charCodeAt(i);

      if (c === CDOT) {
        if (precision === 0) {
          break;
        }

        dotAt = wi;
        out[wi++] = CDOT;

        continue;
      }

      if (dotAt === -1) {
        out[wi++] = c;
      } else {
        if (fracWritten === precision) {
          break;
        }

        out[wi++] = c;
        fracWritten++;

        if (c !== C0) {
          lastNonZeroFrac = wi - 1;
        }
      }
    }

    if (dotAt !== -1) {
      while (fracWritten < precision) {
        out[wi++] = C0;
        fracWritten++;
      }
    }

    if (full && precision > 0) {
      if (dotAt === -1) {
        out[wi++] = CDOT;
        for (let i = 0; i < precision; i++) {
          out[wi++] = C0;
        }
      }
    } else {
      if (dotAt !== -1) {
        wi = lastNonZeroFrac === -1 ? dotAt : lastNonZeroFrac + 1;
      }
    }

    return td.decode(out.subarray(0, wi));
  }
}
